/**
 * Abstract syntax tree for the BroadPaper expression language.
 *
 * The language is intentionally small and has no way to execute host code:
 *   paths        client.firstName   holdings[0].name   item.value
 *   literals     "text"  'text'  12.5  true  false  null
 *   operators    == != < <= > >= + - * / % and or not in ?:
 *   pipes        value | currency:"GBP" | default:"—"
 *   functions    sum(holdings.value)  count(items)  coalesce(a, b)
 */

export type Expr =
  | { kind: "literal"; value: string | number | boolean | null }
  | { kind: "path"; segments: PathSegment[] }
  | { kind: "unary"; op: "not" | "-"; arg: Expr }
  | { kind: "binary"; op: BinaryOp; left: Expr; right: Expr }
  | { kind: "conditional"; test: Expr; then: Expr; else: Expr }
  | { kind: "call"; name: string; args: Expr[] }
  | { kind: "pipe"; input: Expr; name: string; args: Expr[] }
  | { kind: "array"; items: Expr[] };

export type PathSegment = { kind: "prop"; name: string } | { kind: "index"; index: number };

export type BinaryOp = "==" | "!=" | "<" | "<=" | ">" | ">=" | "+" | "-" | "*" | "/" | "%" | "and" | "or" | "in";

export function pathToString(segments: PathSegment[]): string {
  let s = "";
  for (const seg of segments) {
    if (seg.kind === "prop") s += s ? `.${seg.name}` : seg.name;
    else s += `[${seg.index}]`;
  }
  return s;
}

/** Serialises an AST back to canonical source text. */
export function exprToString(e: Expr): string {
  switch (e.kind) {
    case "literal":
      return typeof e.value === "string" ? JSON.stringify(e.value) : String(e.value);
    case "path":
      return pathToString(e.segments);
    case "unary":
      return e.op === "not" ? `not ${wrap(e.arg)}` : `-${wrap(e.arg)}`;
    case "binary":
      return `${wrap(e.left)} ${e.op} ${wrap(e.right)}`;
    case "conditional":
      return `${wrap(e.test)} ? ${wrap(e.then)} : ${wrap(e.else)}`;
    case "call":
      return `${e.name}(${e.args.map(exprToString).join(", ")})`;
    case "pipe":
      return `${exprToString(e.input)} | ${e.name}${e.args.length ? ":" + e.args.map(exprToString).join(":") : ""}`;
    case "array":
      return `[${e.items.map(exprToString).join(", ")}]`;
  }
}

function wrap(e: Expr): string {
  const s = exprToString(e);
  return e.kind === "binary" || e.kind === "conditional" || e.kind === "pipe" ? `(${s})` : s;
}

/** Collects every path referenced by an expression (for validation and dependency tracking). */
export function collectPaths(e: Expr, out: PathSegment[][] = []): PathSegment[][] {
  switch (e.kind) {
    case "path":
      out.push(e.segments);
      break;
    case "unary":
      collectPaths(e.arg, out);
      break;
    case "binary":
      collectPaths(e.left, out);
      collectPaths(e.right, out);
      break;
    case "conditional":
      collectPaths(e.test, out);
      collectPaths(e.then, out);
      collectPaths(e.else, out);
      break;
    case "call":
    case "array":
      for (const a of e.kind === "call" ? e.args : e.items) collectPaths(a, out);
      break;
    case "pipe":
      collectPaths(e.input, out);
      for (const a of e.args) collectPaths(a, out);
      break;
  }
  return out;
}
